import React from 'react';
import { Download, Image } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';

const LogoDownloadPage = () => {
  const logos = [
    {
      name: 'Favicon',
      size: '32x32',
      file: '/favicon-32x32.png',
      description: 'Browser tab icon',
      fileSize: '826 bytes'
    },
    {
      name: 'Small Icon',
      size: '192x192',
      file: '/logo-192x192.png',
      description: 'App icon, email signatures',
      fileSize: '5.6 KB'
    },
    {
      name: 'Medium Icon',
      size: '512x512',
      file: '/logo-512x512.png',
      description: 'Social media, app stores',
      fileSize: '16 KB'
    },
    {
      name: 'High Resolution',
      size: '1024x1024',
      file: '/logo-1024x1024.png',
      description: 'Print materials, presentations',
      fileSize: '35 KB'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 py-20">
      <div className="container mx-auto px-4 max-w-6xl">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Download Your Logo
          </h1>
          <p className="text-xl text-gray-600">
            All PNG files ready for download
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {logos.map((logo, index) => (
            <Card key={index} className="border-2 border-gray-200 hover:border-blue-600 hover:shadow-xl transition-all">
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>{logo.name}</span>
                  <span className="text-sm font-normal text-gray-500">{logo.fileSize}</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="bg-white border-2 border-gray-200 rounded-lg p-8 mb-4 flex items-center justify-center">
                  <img 
                    src={logo.file} 
                    alt={logo.name}
                    className="max-w-full h-auto"
                    style={{ maxHeight: '200px' }}
                  />
                </div>
                <div className="space-y-2 mb-4">
                  <p className="text-sm text-gray-600">
                    <strong>Size:</strong> {logo.size} pixels
                  </p>
                  <p className="text-sm text-gray-600">
                    <strong>Best for:</strong> {logo.description}
                  </p>
                </div>
                <Button asChild className="w-full bg-blue-600 hover:bg-blue-700 text-white">
                  <a href={logo.file} download={`comparebeforeyoubuy-logo-${logo.size}.png`}>
                    <Download className="w-5 h-5 mr-2" />
                    Download PNG
                  </a>
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="mt-12 text-center">
          <Card className="border-2 border-blue-600 bg-blue-50">
            <CardContent className="p-8">
              <Image className="w-12 h-12 text-blue-600 mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Brand Colors</h3>
              <div className="flex justify-center gap-8 flex-wrap mt-6">
                <div className="text-center">
                  <div className="w-20 h-20 rounded-lg mb-2 mx-auto" style={{ backgroundColor: '#2563EB' }}></div>
                  <p className="font-semibold text-gray-900">Primary Blue</p>
                  <p className="text-sm text-gray-600">#2563EB</p>
                </div>
                <div className="text-center">
                  <div className="w-20 h-20 rounded-lg mb-2 mx-auto" style={{ backgroundColor: '#10B981' }}></div>
                  <p className="font-semibold text-gray-900">Primary Green</p>
                  <p className="text-sm text-gray-600">#10B981</p>
                </div>
                <div className="text-center">
                  <div className="w-20 h-20 rounded-lg mb-2 mx-auto border-2 border-gray-300" style={{ backgroundColor: '#FFFFFF' }}></div>
                  <p className="font-semibold text-gray-900">White</p>
                  <p className="text-sm text-gray-600">#FFFFFF</p>
                </div>
                <div className="text-center">
                  <div className="w-20 h-20 rounded-lg mb-2 mx-auto" style={{ backgroundColor: '#111827' }}></div>
                  <p className="font-semibold text-gray-900">Dark Gray</p>
                  <p className="text-sm text-gray-600">#111827</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="mt-8 text-center">
          <Button asChild variant="outline" size="large" className="border-2 border-gray-300">
            <a href="/">
              ← Back to Home
            </a>
          </Button>
        </div>
      </div>
    </div>
  );
};

export default LogoDownloadPage;
